import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";

const MultiEntity: React.FC = () => {
  const { t } = useTranslation();
  const { patients, employees, invoices, suppliers, assets } = useStore();

  // Calculate entity metrics from store data
  const totalPatients = patients.length;
  const totalEmployees = employees.length;
  const totalInvoices = invoices.length;
  const totalSuppliers = suppliers.length;
  const totalAssets = assets.length;
  const totalRevenue = invoices.reduce(
    (sum, invoice) => sum + invoice.amount,
    0
  );

  const entitiesData = [
    {
      id: 1,
      name: "Elaj Hospital - Riyadh",
      type: "Hospital",
      location: "Riyadh, Saudi Arabia",
      status: "Active",
      employees: Math.floor(totalEmployees * 0.6),
      patients: Math.floor(totalPatients * 0.5),
      revenue: Math.floor(totalRevenue * 0.6),
    },
    {
      id: 2,
      name: "Elaj Clinic - Jeddah",
      type: "Clinic",
      location: "Jeddah, Saudi Arabia",
      status: "Active",
      employees: Math.floor(totalEmployees * 0.3),
      patients: Math.floor(totalPatients * 0.3),
      revenue: Math.floor(totalRevenue * 0.3),
    },
    {
      id: 3,
      name: "Elaj Diagnostic Center - Dammam",
      type: "Diagnostic Center",
      location: "Dammam, Saudi Arabia",
      status: "Active",
      employees: Math.floor(totalEmployees * 0.1),
      patients: Math.floor(totalPatients * 0.2),
      revenue: Math.floor(totalRevenue * 0.1),
    },
    {
      id: 4,
      name: "Elaj Hospital - Dubai",
      type: "Hospital",
      location: "Dubai, UAE",
      status: "Planning",
      employees: 0,
      patients: 0,
      revenue: 0,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "success";
      case "Planning":
        return "warning";
      case "Inactive":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("multiEntity.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Multi-entity management and group-level operations
      </Typography>

      {/* Entity Overview */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Entities
              </Typography>
              <Typography variant="h4" color="primary">
                {entitiesData.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Employees
              </Typography>
              <Typography variant="h4" color="success.main">
                {totalEmployees}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Patients
              </Typography>
              <Typography variant="h4" color="info.main">
                {totalPatients}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Group Revenue
              </Typography>
              <Typography variant="h4" color="warning.main">
                SAR {Math.floor(totalRevenue / 1000000)}M
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Entity Management */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Entity Management
          </Typography>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entity</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Employees</TableCell>
                  <TableCell align="right">Patients</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entitiesData.map((entity) => (
                  <TableRow key={entity.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                          {entity.name.charAt(0)}
                        </Avatar>
                        {entity.name}
                      </Box>
                    </TableCell>
                    <TableCell>{entity.type}</TableCell>
                    <TableCell>{entity.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={entity.status}
                        color={getStatusColor(entity.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{entity.employees}</TableCell>
                    <TableCell align="right">{entity.patients}</TableCell>
                    <TableCell align="right">
                      {entity.revenue > 0
                        ? `SAR ${entity.revenue.toLocaleString()}`
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MultiEntity;
